import { createContext, useCallback, useContext, useState, type Dispatch, type SetStateAction } from "react";
import z from "zod";
import type { TOption } from "../components/Autocomplete/Autocomplete";
import { v4 as uuidv4 } from "uuid";
import type { TSelectors, TEntry, TCurve } from "./types";
import type { TProductConfig } from "../../data";
import { useProductById } from "../../data";
import { useAllProductsOptions } from "./hooks";
import { clearDraft, useDraftState } from "./draft";
import { useUserId } from "../../context/auth/hooks";

export const formulaItemValidation = z.object({
  type: z.enum(["product", "selector", "operator", "parenthesis", "number", "month"]),
  value: z.union([z.string(), z.number()]),
  id: z.string().optional(),
  itemId: z.string(),
});

export const entryValidation = z.array(
  z.object({
    id: z.string(),
    formula: z.string(),
    items: z.array(formulaItemValidation),
    repetitions: z.number(),
    manualEntry: z.boolean(),
    valid: z.boolean().optional(),
    gapBefore: z.number().optional(),
  }),
);

export const curveFormValidation = z.object({
  curveId: z.string().min(1),
  curvePackage: z.string().min(1),
  curveName: z.string().min(1), // description
  displayName: z.string().min(1), // name
  curveType: z.string().min(1),
  uom: z.string().min(1),
  periodAggregation: z.string().min(1),
  productType: z.string().optional(),
  parent: z.string().optional(),
  region: z.string().optional(),
  commodity: z.string().optional(),
  eod: z.string().optional(),
  global: z.array(z.string()).optional(),
  formulas: entryValidation,
});

export type TCurveForm = z.infer<typeof curveFormValidation>;

export type TCurveFormContext = TCurveForm & {
  resetCurveForm: () => void;
  setCurveId: Dispatch<SetStateAction<string>>;
  setCurvePackage: Dispatch<SetStateAction<string | undefined>>;
  setCurveName: Dispatch<SetStateAction<string | undefined>>;
  setDisplayName: Dispatch<SetStateAction<string | undefined>>;
  setCurveType: Dispatch<SetStateAction<string | undefined>>;
  setUom: Dispatch<SetStateAction<string | undefined>>;
  setPeriodAggregation: Dispatch<SetStateAction<string | undefined>>;
  setProductType: Dispatch<SetStateAction<string | undefined>>;
  setParent: Dispatch<SetStateAction<string | undefined>>;
  setRegion: Dispatch<SetStateAction<string | undefined>>;
  setCommodity: Dispatch<SetStateAction<string | undefined>>;
  setEod: Dispatch<SetStateAction<string | undefined>>;
  setGlobal: Dispatch<SetStateAction<string[] | undefined>>;
  setFormulas: Dispatch<SetStateAction<TEntry[]>>;

  configs: TProductConfig[] | undefined;
  setConfigs: Dispatch<SetStateAction<TProductConfig[] | undefined>>;

  inputCurves?: TOption[];
  setInputCurves?: Dispatch<SetStateAction<TOption[] | undefined>>;

  editingCurve: boolean;

  /**
   * True when the curve currently loaded for editing is a Snapshot/EOD
   * Curve — i.e. `artis_type === "eod"` or `curve_type === "snapshot"`.
   * These curves can be written to by automated processes (e.g. Auto-Snaps),
   * so their formula/definition must stay locked; only Curve details remain
   * editable.
   */
  isSnapshotOrEodCurve: boolean;

  selectors?: TSelectors;
  setSelectors: Dispatch<SetStateAction<TSelectors | undefined>>;
  products?: TCurve[];
  setProducts: Dispatch<SetStateAction<TCurve[] | undefined>>;

  repeatFinalBlock?: boolean;
  setRepeatFinalBlock?: Dispatch<SetStateAction<boolean>>;

  pendingInputCurves?: TOption[];
  setPendingInputCurves?: Dispatch<SetStateAction<TOption[] | undefined>>;
  pendingGlobalCurves?: TOption[];
  setPendingGlobalCurves?: Dispatch<SetStateAction<TOption[] | undefined>>;

  /** Package id of the curve the user copied from (used to surface it as a Suggested folder). */
  copiedFromPackage?: string;
  setCopiedFromPackage: Dispatch<SetStateAction<string | undefined>>;

  /** When true, the save flow also creates a Δ-prefixed delta curve linked to the selected EOD. */
  createDeltaDay?: boolean;
  setCreateDeltaDay: Dispatch<SetStateAction<boolean>>;

  /**
   * Snapshot of the form values captured immediately after applying an
   * existing curve (Edit or Copy). Powers the "Reset changes" affordance:
   * when set, the button becomes available and is enabled while the live
   * form differs from this snapshot.
   */
  formSnapshot?: TFormSnapshot;
  setFormSnapshot: Dispatch<SetStateAction<TFormSnapshot | undefined>>;

  /** History key of the entry already applied to this draft. See `pendingArrivalAction`. */
  draftNavKey?: string;
  setDraftNavKey: Dispatch<SetStateAction<string | undefined>>;

  /** Active tab in narrow (tablet/mobile) curve management layout only. */
  narrowCurveMgmtActiveTab: "segments" | "details";
  setNarrowCurveMgmtActiveTab: Dispatch<SetStateAction<"segments" | "details">>;

  /** Shared UI toggle used by both Curve Details and Save modal. */
  useCurveNameAsDisplay: boolean;
  setUseCurveNameAsDisplay: Dispatch<SetStateAction<boolean>>;
};

export type TFormSnapshot = {
  mode: "edit" | "copy";
  curvePackage: string;
  curveName: string;
  displayName: string;
  curveType: string;
  uom: string;
  periodAggregation: string;
  productType: string;
  parent: string;
  region: string;
  commodity: string;
  eod: string;
  global: string[];
  formulas: TEntry[];
  /** Pending autocomplete option lists used by downstream effects on reset. */
  pendingInputCurves: TOption[];
  pendingGlobalCurves: TOption[];
};

const CurveFormContext = createContext<TCurveFormContext>({
  curveId: "",
  curvePackage: "",
  curveName: "",
  displayName: "",
  curveType: "live",
  uom: "",
  periodAggregation: "",
  productType: "",
  parent: "",
  region: "",
  commodity: "",
  eod: "",
  global: [],
  configs: [],
  formulas: [],
  selectors: [],
  products: [],
  editingCurve: false,
  isSnapshotOrEodCurve: false,
  resetCurveForm: () => {},
  setCurveId: () => {},
  setCurvePackage: () => {},
  setCurveName: () => {},
  setDisplayName: () => {},
  setCurveType: () => {},
  setUom: () => {},
  setPeriodAggregation: () => {},
  setProductType: () => {},
  setParent: () => {},
  setRegion: () => {},
  setCommodity: () => {},
  setEod: () => {},
  setGlobal: () => {},
  setConfigs: () => {},
  setFormulas: () => {},
  setSelectors: () => {},
  setProducts: () => {},
  setCopiedFromPackage: () => {},
  createDeltaDay: false,
  setCreateDeltaDay: () => {},
  formSnapshot: undefined,
  setFormSnapshot: () => {},
  draftNavKey: undefined,
  setDraftNavKey: () => {},
  narrowCurveMgmtActiveTab: "segments",
  setNarrowCurveMgmtActiveTab: () => {},
  useCurveNameAsDisplay: true,
  setUseCurveNameAsDisplay: () => {},
});

export const CurveFormProvider = CurveFormContext.Provider;

export const useCurveFormContext = () => useContext(CurveFormContext);

export function CurveFormState({ children }: { children: React.ReactNode }) {
  // Persist the user-editable form state per-user so an in-progress create
  // or edit isn't lost on page reload. Derived/transient fields (configs,
  // selectors, products, inputCurves, pendingGlobalCurves,
  // pendingInputCurves) deliberately stay non-persisted — they are
  // recomputed from the persisted formulas + curveId on reload.
  const userId = useUserId();
  const [curveId, setCurveId] = useDraftState<string>(userId, "curveId", uuidv4);
  const [curvePackage, setCurvePackage] = useDraftState<string | undefined>(userId, "curvePackage", undefined);
  const [curveName, setCurveName] = useDraftState<string | undefined>(userId, "curveName", undefined);
  const [displayName, setDisplayName] = useDraftState<string | undefined>(userId, "displayName", undefined);
  /** Default matches `curveTypes` first option (Live) in hooks.ts */
  const [curveType, setCurveType] = useDraftState<string | undefined>(userId, "curveType", "live");
  const [uom, setUom] = useDraftState<string | undefined>(userId, "uom", undefined);
  const [periodAggregation, setPeriodAggregation] = useDraftState<string | undefined>(
    userId,
    "periodAggregation",
    undefined,
  );
  const [productType, setProductType] = useDraftState<string | undefined>(userId, "productType", undefined);
  const [parent, setParent] = useDraftState<string | undefined>(userId, "parent", undefined);
  const [region, setRegion] = useDraftState<string | undefined>(userId, "region", undefined);
  const [commodity, setCommodity] = useDraftState<string | undefined>(userId, "commodity", undefined);
  const [eod, setEod] = useDraftState<string | undefined>(userId, "eod", undefined);
  const [global, setGlobal] = useDraftState<string[] | undefined>(userId, "global", undefined);
  const [inputCurves, setInputCurves] = useState<TOption[] | undefined>();
  const [selectors, setSelectors] = useState<TSelectors | undefined>();
  const [products, setProducts] = useState<TCurve[] | undefined>();
  const [configs, setConfigs] = useState<TProductConfig[] | undefined>();
  const [formulas, setFormulas] = useDraftState<TEntry[]>(userId, "formulas", []);
  const [repeatFinalBlock, setRepeatFinalBlock] = useDraftState<boolean>(userId, "repeatFinalBlock", false);

  const [pendingInputCurves, setPendingInputCurves] = useState<TOption[] | undefined>();
  const [pendingGlobalCurves, setPendingGlobalCurves] = useState<TOption[] | undefined>();
  const [copiedFromPackage, setCopiedFromPackage] = useDraftState<string | undefined>(
    userId,
    "copiedFromPackage",
    undefined,
  );
  const [createDeltaDay, setCreateDeltaDay] = useDraftState<boolean>(userId, "createDeltaDay", false);
  const [formSnapshot, setFormSnapshot] = useDraftState<TFormSnapshot | undefined>(userId, "formSnapshot", undefined);
  const [draftNavKey, setDraftNavKey] = useDraftState<string | undefined>(userId, "draftNavKey", undefined);
  const [narrowCurveMgmtActiveTab, setNarrowCurveMgmtActiveTab] = useDraftState<"segments" | "details">(
    userId,
    "narrowCurveMgmtActiveTab",
    "segments",
  );
  const [useCurveNameAsDisplay, setUseCurveNameAsDisplay] = useDraftState<boolean>(
    userId,
    "useCurveNameAsDisplay",
    true,
  );

  const editingCurve = useAllProductsOptions().data?.curves.some((p) => p.metadata.id === curveId);
  // `useProductById` reads from the same cached products query as
  // `useAllProductsOptions` above, so this doesn't trigger a separate fetch.
  const editingProduct = useProductById(curveId).data;
  const isSnapshotOrEodCurve =
    Boolean(editingCurve) && (editingProduct?.artis_type === "eod" || editingProduct?.curve_type === "snapshot");

  const resetCurveForm = useCallback(() => {
    // Wipe the persisted draft first so the new defaults below aren't
    // immediately overwritten by stale storage when this component (or a
    // sibling render) reads it.
    clearDraft(userId);

    setCurveId(uuidv4());

    setCurvePackage(undefined);
    setCurveName(undefined);
    setDisplayName(undefined);
    setCurveType("live");
    setUom(undefined);
    setPeriodAggregation(undefined);
    setProductType(undefined);
    setParent(undefined);
    setRegion(undefined);
    setCommodity(undefined);
    setEod(undefined);
    setGlobal(undefined);
    setInputCurves(undefined);
    setSelectors(undefined);
    setProducts(undefined);
    setConfigs(undefined);
    setRepeatFinalBlock(false);

    setFormulas([]);

    setPendingGlobalCurves([]);
    setPendingInputCurves([]);
    setCopiedFromPackage(undefined);
    setCreateDeltaDay(false);
    setFormSnapshot(undefined);
    setNarrowCurveMgmtActiveTab("segments");
    setUseCurveNameAsDisplay(true);
  }, [
    userId,
    setCurveId,
    setCurvePackage,
    setCurveName,
    setDisplayName,
    setCurveType,
    setUom,
    setPeriodAggregation,
    setProductType,
    setParent,
    setRegion,
    setCommodity,
    setEod,
    setGlobal,
    setRepeatFinalBlock,
    setFormulas,
    setCopiedFromPackage,
    setCreateDeltaDay,
    setFormSnapshot,
    setNarrowCurveMgmtActiveTab,
    setUseCurveNameAsDisplay,
  ]);

  return (
    <CurveFormProvider
      value={{
        resetCurveForm,
        curveId,
        setCurveId,
        curvePackage: curvePackage || "",
        setCurvePackage,
        curveName: curveName || "",
        setCurveName,
        displayName: displayName || "",
        setDisplayName,
        curveType: curveType || "",
        setCurveType,
        uom: uom || "",
        setUom,
        periodAggregation: periodAggregation || "",
        setPeriodAggregation,
        productType: productType || "",
        setProductType,
        parent: parent || "",
        setParent,
        region: region || "",
        setRegion,
        commodity: commodity || "",
        setCommodity,
        eod: eod || "",
        setEod,
        global: global || [],
        setGlobal,
        inputCurves,
        setInputCurves,
        selectors,
        setSelectors,
        products,
        setProducts,
        formulas,
        setFormulas,
        repeatFinalBlock,
        setRepeatFinalBlock,
        pendingInputCurves,
        setPendingInputCurves,
        pendingGlobalCurves,
        setPendingGlobalCurves,
        configs,
        setConfigs,
        editingCurve,
        isSnapshotOrEodCurve,
        copiedFromPackage,
        setCopiedFromPackage,
        createDeltaDay,
        setCreateDeltaDay,
        formSnapshot,
        setFormSnapshot,
        draftNavKey,
        setDraftNavKey,
        narrowCurveMgmtActiveTab,
        setNarrowCurveMgmtActiveTab,
        useCurveNameAsDisplay,
        setUseCurveNameAsDisplay,
      }}
    >
      {children}
    </CurveFormProvider>
  );
}
